import { dynamicToolActivityKind } from './tool-identity';
import type { DynamicToolPart, ToolActivityKind, ToolActivityPart } from './types';

export function toolGroupState(parts: readonly DynamicToolPart[]): DynamicToolPart['state'] {
  if (parts.some((part) => part.state === 'input-streaming')) return 'input-streaming';
  if (parts.some((part) => part.state === 'input-available')) return 'input-available';
  if (parts.some((part) => part.state === 'output-error')) return 'output-error';
  return 'output-available';
}

export function toolActivitySummary(parts: readonly ToolActivityPart[]): {
  action: string;
  details: string;
  type: `tool-${string}`;
} {
  const tools = parts.filter((part): part is DynamicToolPart => part.type === 'dynamic-tool');
  const kinds = new Set<ToolActivityKind>();
  const annotatedToolCallIds = new Set(
    parts.flatMap((part) =>
      part.type === 'data-diff' || part.type === 'data-terminal' ? [part.data.toolCallId] : [],
    ),
  );

  // A diff is authoritative: some ACP agents expose their mutation tool under a
  // generic name such as `other`, while still emitting the edited file content.
  if (parts.some((part) => part.type === 'data-diff')) kinds.add('file-changes');
  for (const tool of tools) {
    const kind = dynamicToolActivityKind(tool);
    if (kind !== 'tools' || !annotatedToolCallIds.has(tool.toolCallId)) kinds.add(kind);
  }
  if (parts.some((part) => part.type === 'data-terminal')) kinds.add('commands');

  const detailSegments: string[] = [];
  const diffPaths = new Set(
    parts.filter((part) => part.type === 'data-diff').map((part) => part.data.path),
  );
  const fileChangeToolCount = tools.filter(
    (part) => dynamicToolActivityKind(part) === 'file-changes',
  ).length;
  const fileChangeCount = diffPaths.size > 0 ? diffPaths.size : fileChangeToolCount;
  const terminalToolCallIds = new Set(
    parts.filter((part) => part.type === 'data-terminal').map((part) => part.data.toolCallId),
  );
  const commandCount = Math.max(
    terminalToolCallIds.size,
    tools.filter((part) => dynamicToolActivityKind(part) === 'commands').length,
  );
  const toolCount = tools.filter(
    (part) =>
      dynamicToolActivityKind(part) === 'tools' && !annotatedToolCallIds.has(part.toolCallId),
  ).length;

  if (fileChangeCount > 0) {
    detailSegments.push(`${fileChangeCount} file${fileChangeCount === 1 ? '' : 's'}`);
  } else if (kinds.has('exploration')) {
    const explorationCount = tools.filter(
      (part) => dynamicToolActivityKind(part) === 'exploration',
    ).length;
    detailSegments.push(`${explorationCount} file${explorationCount === 1 ? '' : 's'}`);
  }
  if (commandCount > 0) {
    detailSegments.push(`${commandCount} command${commandCount === 1 ? '' : 's'}`);
  }
  if (kinds.has('web')) detailSegments.push('searched web');
  if (toolCount > 0) detailSegments.push(`${toolCount} tool${toolCount === 1 ? '' : 's'}`);

  const active = tools.some(
    (part) => part.state === 'input-streaming' || part.state === 'input-available',
  );
  const onlyKind = kinds.size === 1 ? [...kinds][0] : undefined;
  const action =
    onlyKind === 'file-changes'
      ? active
        ? 'Editing'
        : 'Edited'
      : onlyKind === 'commands'
        ? active
          ? 'Running'
          : 'Ran'
        : onlyKind === 'web'
          ? active
            ? 'Searching'
            : 'Searched'
          : active
            ? 'Exploring'
            : 'Explored';
  const firstKind = [...kinds][0] ?? 'tools';
  const type =
    firstKind === 'file-changes'
      ? 'tool-edit'
      : firstKind === 'exploration'
        ? 'tool-read'
        : firstKind === 'commands'
          ? 'tool-terminal'
          : firstKind === 'web'
            ? 'tool-web'
            : 'tool-group';

  return { action, details: detailSegments.join(', ') || `${tools.length} tools`, type };
}
